import { useEffect, useState } from "react";
import Navbar from "../../../components/Navbar";

interface NotificationFilters {
    has_membership?: boolean;
    user_ids?: string[];
}

interface Notification {
    _id: string;
    title: string;
    body: string;
    filters?: NotificationFilters;
    status: "draft" | "sent" | "failed";
    createdAt?: string;
    updatedAt?: string;
}

interface User {
    _id: string;
    full_name?: string;
    email?: string;
    phone_prefix?: string;
    phone_number?: string;
    wallet_balance?: number;
}

interface Pagination {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
}

const API_BASE_URL = "https://www.frendrops.in";

export default function GlobalNotifications() {
    const [notifications, setNotifications] = useState<Notification[]>([]);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("");

    const [page, setPage] = useState(1);
    const [limit] = useState(20);

    const [pagination, setPagination] = useState<Pagination>({
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 1,
    });

    const [showModal, setShowModal] = useState(false);

    const [editingId, setEditingId] = useState<string | null>(null);

    const [title, setTitle] = useState("");
    const [body, setBody] = useState("");

    const [hasMembership, setHasMembership] = useState(false);

    const [userIdsText, setUserIdsText] = useState("");

    const [users, setUsers] = useState<User[]>([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [selectingAllUsers, setSelectingAllUsers] = useState(false);
    const [usersError, setUsersError] = useState("");
    const [userSearchInput, setUserSearchInput] = useState("");
    const [userSearch, setUserSearch] = useState("");
    const [userPage, setUserPage] = useState(1);
    const [userTotalPages, setUserTotalPages] = useState(1);
    const [userTotal, setUserTotal] = useState(0);

    // --------------------------------------------------
    // GET ALL NOTIFICATIONS
    // --------------------------------------------------

    const fetchNotifications = async () => {
        try {
            setLoading(true);

            const params = new URLSearchParams();

            params.append("page", String(page));
            params.append("limit", String(limit));

            if (status) {
                params.append("status", status);
            }

            if (search.trim()) {
                params.append("search", search.trim());
            }

            const response = await fetch(
                `${API_BASE_URL}/api/global-notifications?${params.toString()}`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            const result = await response.json();

            console.log("Global notifications API response:", result);

            if (!response.ok) {
                throw new Error(
                    result.message || "Failed to fetch notifications"
                );
            }

            /*
             * Handle different possible API response structures
             */

            let notificationList: Notification[] = [];

            if (Array.isArray(result.data)) {
                // {
                //   success: true,
                //   data: [...]
                // }
                notificationList = result.data;
            } else if (Array.isArray(result.data?.notifications)) {
                // {
                //   success: true,
                //   data: {
                //     notifications: [...]
                //   }
                // }
                notificationList = result.data.notifications;
            } else if (Array.isArray(result.notifications)) {
                // {
                //   success: true,
                //   notifications: [...]
                // }
                notificationList = result.notifications;
            }

            setNotifications(notificationList);

            /*
             * Handle pagination
             */

            const paginationData =
                result.pagination ||
                result.data?.pagination;

            if (paginationData) {
                setPagination({
                    page: paginationData.page ?? page,
                    limit: paginationData.limit ?? limit,
                    total: paginationData.total ?? notificationList.length,
                    totalPages:
                        paginationData.totalPages ??
                        Math.ceil(
                            (paginationData.total ?? notificationList.length) /
                            (paginationData.limit ?? limit)
                        ),
                });
            } else {
                setPagination({
                    page,
                    limit,
                    total: notificationList.length,
                    totalPages: Math.max(
                        1,
                        Math.ceil(notificationList.length / limit)
                    ),
                });
            }
        } catch (error) {
            console.error(
                "Fetch notifications error:",
                error
            );

            setNotifications([]);

            setPagination({
                page: 1,
                limit,
                total: 0,
                totalPages: 1,
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchNotifications();
    }, [page, status]);

    // --------------------------------------------------
    // GET USERS FOR TARGET SELECTION
    // --------------------------------------------------

    const fetchUsers = async () => {
        try {
            setUsersLoading(true);
            setUsersError("");

            const params = new URLSearchParams({
                page: String(userPage),
                limit: "10",
            });

            if (userSearch) {
                params.append("search", userSearch);
            }

            const response = await fetch(
                `${API_BASE_URL}/api/users?${params.toString()}`,
                {
                    headers: {
                        Authorization: `Bearer ${
                            localStorage.getItem("adminToken") ||
                            localStorage.getItem("token") ||
                            ""
                        }`,
                    },
                }
            );

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.message || "Failed to fetch users");
            }

            const data = result.data || result;
            const userList = Array.isArray(data.users) ? data.users : [];
            const userPagination = data.pagination || {};

            setUsers(userList);
            setUserTotal(userPagination.totalItems || userList.length);
            setUserTotalPages(
                Math.max(1, userPagination.totalPages || 1)
            );
        } catch (error: any) {
            setUsers([]);
            setUsersError(error.message || "Failed to fetch users");
        } finally {
            setUsersLoading(false);
        }
    };

    useEffect(() => {
        if (showModal) {
            fetchUsers();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showModal, userPage, userSearch]);

    const selectedUserIds = userIdsText
        .split("\n")
        .map((id) => id.trim())
        .filter(Boolean);

    const toggleUser = (userId: string) => {
        const nextIds = selectedUserIds.includes(userId)
            ? selectedUserIds.filter((id) => id !== userId)
            : [...selectedUserIds, userId];

        setUserIdsText(nextIds.join("\n"));
    };

    const selectAllUsers = async () => {
        try {
            setSelectingAllUsers(true);
            setUsersError("");

            const allUserIds: string[] = [];
            let nextPage = 1;
            let totalPages = 1;

            do {
                const params = new URLSearchParams({
                    page: String(nextPage),
                    limit: "100",
                });

                const response = await fetch(
                    `${API_BASE_URL}/api/users?${params.toString()}`,
                    {
                        headers: {
                            Authorization: `Bearer ${
                                localStorage.getItem("adminToken") ||
                                localStorage.getItem("token") ||
                                ""
                            }`,
                        },
                    }
                );

                const result = await response.json();

                if (!response.ok) {
                    throw new Error(
                        result.message || "Failed to select all users"
                    );
                }

                const data = result.data || result;
                const pageUsers: User[] = Array.isArray(data.users)
                    ? data.users
                    : [];

                allUserIds.push(...pageUsers.map((user) => user._id));
                totalPages = Math.max(
                    1,
                    Number(data.pagination?.totalPages) || 1
                );
                nextPage += 1;
            } while (nextPage <= totalPages);

            setUserIdsText(Array.from(new Set(allUserIds)).join("\n"));
        } catch (error: any) {
            setUsersError(error.message || "Failed to select all users");
        } finally {
            setSelectingAllUsers(false);
        }
    };

    const clearSelectedUsers = () => {
        setUserIdsText("");
    };

    const toggleAllVisibleUsers = () => {
        const visibleIds = users.map((user) => user._id);
        const allVisibleSelected = visibleIds.every((id) =>
            selectedUserIds.includes(id)
        );

        const nextIds = allVisibleSelected
            ? selectedUserIds.filter((id) => !visibleIds.includes(id))
            : Array.from(new Set([...selectedUserIds, ...visibleIds]));

        setUserIdsText(nextIds.join("\n"));
    };

    const handleUserSearch = () => {
        setUserPage(1);
        setUserSearch(userSearchInput.trim());
    };

    // --------------------------------------------------
    // RESET FORM
    // --------------------------------------------------

    const resetForm = () => {
        setTitle("");
        setBody("");
        setHasMembership(false);
        setUserIdsText("");
        setEditingId(null);
    };

    // --------------------------------------------------
    // OPEN CREATE MODAL
    // --------------------------------------------------

    const handleCreate = () => {
        resetForm();
        setShowModal(true);
    };

    // --------------------------------------------------
    // OPEN EDIT MODAL
    // --------------------------------------------------

    const handleEdit = (notification: Notification) => {
        setEditingId(notification._id);

        setTitle(notification.title);
        setBody(notification.body);

        setHasMembership(
            notification.filters?.has_membership ?? false
        );

        setUserIdsText(
            notification.filters?.user_ids?.join("\n") || ""
        );

        setShowModal(true);
    };

    // --------------------------------------------------
    // CREATE / UPDATE
    // --------------------------------------------------

    const handleSave = async () => {
        if (!title.trim()) {
            alert("Please enter notification title");
            return;
        }

        if (!body.trim()) {
            alert("Please enter notification body");
            return;
        }

        try {
            setSaving(true);

            const userIds = userIdsText
                .split("\n")
                .map((id) => id.trim())
                .filter(Boolean);

            const payload = {
                title: title.trim(),
                body: body.trim(),
                filters: {
                    has_membership: hasMembership,
                    user_ids: userIds,
                },
            };

            const url = editingId
                ? `${API_BASE_URL}/api/global-notifications/${editingId}`
                : `${API_BASE_URL}/api/global-notifications/create`;

            const response = await fetch(url, {
                method: editingId ? "PUT" : "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
                body: JSON.stringify(payload),
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.message || "Failed to save notification");
            }

            alert(
                editingId
                    ? "Notification updated successfully"
                    : "Notification created successfully"
            );

            setShowModal(false);
            resetForm();

            fetchNotifications();
        } catch (error: any) {
            console.error("Save notification error:", error);

            alert(error.message || "Something went wrong");
        } finally {
            setSaving(false);
        }
    };

    // --------------------------------------------------
    // SEND NOTIFICATION
    // --------------------------------------------------

    const handleSend = async (id: string) => {
        const confirmed = window.confirm(
            "Are you sure you want to send this notification?"
        );

        if (!confirmed) return;

        try {
            const response = await fetch(
                `${API_BASE_URL}/api/global-notifications/${id}/send`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                }
            );

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.message || "Failed to send notification");
            }

            alert("Notification sent successfully");

            fetchNotifications();
        } catch (error: any) {
            console.error("Send notification error:", error);

            alert(error.message || "Failed to send notification");
        }
    };

    // --------------------------------------------------
    // DELETE NOTIFICATION
    // --------------------------------------------------

    const handleDelete = async (notification: Notification) => {
        const confirmed = window.confirm(
            `Delete notification "${notification.title}"? This action cannot be undone.`
        );

        if (!confirmed) return;

        try {
            const response = await fetch(
                `${API_BASE_URL}/api/global-notifications/${notification._id}`,
                {
                    method: "DELETE",
                    headers: {
                        Authorization: `Bearer ${
                            localStorage.getItem("adminToken") ||
                            localStorage.getItem("token") ||
                            ""
                        }`,
                    },
                }
            );

            const responseText = await response.text();
            const result = responseText ? JSON.parse(responseText) : {};

            if (!response.ok) {
                throw new Error(
                    result.message || "Failed to delete notification"
                );
            }

            alert("Notification deleted successfully");

            if (notifications.length === 1 && page > 1) {
                setPage((currentPage) => currentPage - 1);
            } else {
                fetchNotifications();
            }
        } catch (error: any) {
            console.error("Delete notification error:", error);
            alert(error.message || "Failed to delete notification");
        }
    };

    // --------------------------------------------------
    // SEARCH
    // --------------------------------------------------

    const handleSearch = () => {
        setPage(1);
        fetchNotifications();
    };

    const handleSearchKeyDown = (
        event: React.KeyboardEvent<HTMLInputElement>
    ) => {
        if (event.key === "Enter") {
            handleSearch();
        }
    };

    // --------------------------------------------------
    // DATE FORMAT
    // --------------------------------------------------

    const formatDate = (date?: string) => {
        if (!date) return "-";

        return new Date(date).toLocaleString("en-IN", {
            dateStyle: "medium",
            timeStyle: "short",
        });
    };

    return (
        <div className="min-h-screen bg-white">
            {/* ==================================================
              PAGE
          ================================================== */}
            <Navbar />
            <div className="min-h-screen bg-gray-50 p-4 md:p-8">

                {/* HEADER */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">

                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">
                            Global Notifications
                        </h1>

                        <p className="text-gray-500 mt-1">
                            Create and manage notifications for your users.
                        </p>
                    </div>

                    <button
                        onClick={handleCreate}
                        className="bg-violet-600 hover:bg-violet-700 text-white px-5 py-3 rounded-xl font-semibold transition"
                    >
                        + Create Notification
                    </button>

                </div>

                {/* FILTERS */}
                <div className="bg-white rounded-2xl shadow-sm border p-4 mb-6">

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

                        {/* SEARCH */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Search
                            </label>

                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={handleSearchKeyDown}
                                placeholder="Search title or body..."
                                className="w-full border border-gray-300 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-violet-500"
                            />
                        </div>

                        {/* STATUS */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Status
                            </label>

                            <select
                                value={status}
                                onChange={(e) => {
                                    setStatus(e.target.value);
                                    setPage(1);
                                }}
                                className="w-full border border-gray-300 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-violet-500"
                            >
                                <option value="">All Status</option>
                                <option value="draft">Draft</option>
                                <option value="sent">Sent</option>
                                <option value="failed">Failed</option>
                            </select>
                        </div>

                        {/* SEARCH BUTTON */}
                        <div className="flex items-end">

                            <button
                                onClick={handleSearch}
                                className="w-full bg-gray-900 hover:bg-gray-800 text-white rounded-xl px-4 py-3 font-medium"
                            >
                                Search
                            </button>

                        </div>

                    </div>

                </div>

                {/* TABLE */}
                <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">

                    <div className="overflow-x-auto">

                        <table className="w-full">

                            <thead className="bg-gray-50 border-b">

                                <tr>

                                    <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">
                                        Notification
                                    </th>

                                    <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">
                                        Target
                                    </th>

                                    <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">
                                        Status
                                    </th>

                                    <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">
                                        Created
                                    </th>

                                    <th className="text-right px-6 py-4 text-sm font-semibold text-gray-600">
                                        Actions
                                    </th>

                                </tr>

                            </thead>

                            <tbody className="divide-y">

                                {loading ? (

                                    <tr>
                                        <td
                                            colSpan={5}
                                            className="text-center py-12 text-gray-500"
                                        >
                                            Loading notifications...
                                        </td>
                                    </tr>

                                ) : notifications.length === 0 ? (

                                    <tr>
                                        <td
                                            colSpan={5}
                                            className="text-center py-12 text-gray-500"
                                        >
                                            No notifications found.
                                        </td>
                                    </tr>

                                ) : (

                                    notifications.map((notification) => (

                                        <tr
                                            key={notification._id}
                                            className="hover:bg-gray-50"
                                        >

                                            {/* TITLE */}
                                            <td className="px-6 py-5">

                                                <div className="font-semibold text-gray-900">
                                                    {notification.title}
                                                </div>

                                                <div className="text-sm text-gray-500 mt-1 max-w-md">
                                                    {notification.body}
                                                </div>

                                            </td>

                                            {/* TARGET */}
                                            <td className="px-6 py-5">

                                                <div className="space-y-1">

                                                    <span className="inline-flex bg-violet-100 text-violet-700 px-3 py-1 rounded-full text-xs font-medium">
                                                        {notification.filters?.has_membership
                                                            ? "Members"
                                                            : "Non-members"}
                                                    </span>

                                                    {notification.filters?.user_ids &&
                                                        notification.filters.user_ids.length > 0 && (

                                                            <div className="text-xs text-gray-500">
                                                                {notification.filters.user_ids.length}{" "}
                                                                specific user(s)
                                                            </div>

                                                        )}

                                                </div>

                                            </td>

                                            {/* STATUS */}
                                            <td className="px-6 py-5">

                                                <span
                                                    className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${notification.status === "sent"
                                                            ? "bg-green-100 text-green-700"
                                                            : notification.status === "failed"
                                                                ? "bg-red-100 text-red-700"
                                                                : "bg-yellow-100 text-yellow-700"
                                                        }`}
                                                >
                                                    {notification.status.toUpperCase()}
                                                </span>

                                            </td>

                                            {/* DATE */}
                                            <td className="px-6 py-5 text-sm text-gray-500">
                                                {formatDate(notification.createdAt)}
                                            </td>

                                            {/* ACTIONS */}
                                            <td className="px-6 py-5">

                                                <div className="flex justify-end gap-2">

                                                    {/* EDIT */}
                                                    {notification.status !== "sent" && (

                                                        <button
                                                            onClick={() => handleEdit(notification)}
                                                            className="px-3 py-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 text-sm font-medium"
                                                        >
                                                            Edit
                                                        </button>

                                                    )}

                                                    {/* SEND */}
                                                    {notification.status === "draft" && (

                                                        <button
                                                            onClick={() =>
                                                                handleSend(notification._id)
                                                            }
                                                            className="px-3 py-2 rounded-lg bg-green-50 text-green-600 hover:bg-green-100 text-sm font-medium"
                                                        >
                                                            Send
                                                        </button>

                                                    )}

                                                    {/* DELETE */}
                                                    <button
                                                        onClick={() =>
                                                            handleDelete(notification)
                                                        }
                                                        className="px-3 py-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 text-sm font-medium"
                                                    >
                                                        Delete
                                                    </button>

                                                </div>

                                            </td>

                                        </tr>

                                    ))

                                )}

                            </tbody>

                        </table>

                    </div>

                    {/* PAGINATION */}
                    <div className="border-t px-6 py-4 flex items-center justify-between">

                        <div className="text-sm text-gray-500">
                            Page {pagination.page} of {pagination.totalPages}
                        </div>

                        <div className="flex gap-2">

                            <button
                                disabled={page <= 1}
                                onClick={() => setPage((prev) => prev - 1)}
                                className="px-4 py-2 border rounded-lg disabled:opacity-40 hover:bg-gray-50"
                            >
                                Previous
                            </button>

                            <button
                                disabled={page >= pagination.totalPages}
                                onClick={() => setPage((prev) => prev + 1)}
                                className="px-4 py-2 border rounded-lg disabled:opacity-40 hover:bg-gray-50"
                            >
                                Next
                            </button>

                        </div>

                    </div>

                </div>

                {/* CREATE / EDIT MODAL */}
                {showModal && (

                    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">

                        <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl">

                            {/* MODAL HEADER */}
                            <div className="flex items-center justify-between px-6 py-5 border-b">

                                <h2 className="text-xl font-bold text-gray-900">
                                    {editingId
                                        ? "Edit Notification"
                                        : "Create Notification"}
                                </h2>

                                <button
                                    onClick={() => {
                                        setShowModal(false);
                                        resetForm();
                                    }}
                                    className="text-gray-500 hover:text-gray-900 text-2xl"
                                >
                                    ×
                                </button>

                            </div>

                            {/* MODAL BODY */}
                            <div className="p-6 space-y-5">

                                {/* TITLE */}
                                <div>

                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Title
                                    </label>

                                    <input
                                        type="text"
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        placeholder="Special Offer"
                                        className="w-full border border-gray-300 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-violet-500"
                                    />

                                </div>

                                {/* BODY */}
                                <div>

                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Message
                                    </label>

                                    <textarea
                                        value={body}
                                        onChange={(e) => setBody(e.target.value)}
                                        rows={4}
                                        placeholder="Get 20% off on all memberships"
                                        className="w-full border border-gray-300 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-violet-500 resize-none"
                                    />

                                </div>

                                {/* MEMBERSHIP */}
                                <div>

                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Membership Filter
                                    </label>

                                    <label className="flex items-center gap-3 border rounded-xl p-4 cursor-pointer hover:bg-gray-50">

                                        <input
                                            type="checkbox"
                                            checked={hasMembership}
                                            onChange={(e) =>
                                                setHasMembership(e.target.checked)
                                            }
                                            className="w-5 h-5 accent-violet-600"
                                        />

                                        <div>
                                            <div className="font-medium text-gray-900">
                                                Has Membership
                                            </div>

                                            <div className="text-sm text-gray-500">
                                                Send notification to users based on membership
                                                status.
                                            </div>
                                        </div>

                                    </label>

                                </div>

                                {/* USER SELECTION */}
                                <div>
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700">
                                                Select Specific Users
                                            </label>
                                            <p className="text-xs text-gray-500 mt-1">
                                                {selectedUserIds.length} selected out of {userTotal} users
                                            </p>
                                        </div>

                                        <div className="flex flex-wrap gap-2">
                                            <button
                                                type="button"
                                                onClick={toggleAllVisibleUsers}
                                                disabled={users.length === 0}
                                                className="text-sm font-semibold text-violet-700 hover:text-violet-900 disabled:opacity-50"
                                            >
                                                {users.length > 0 &&
                                                users.every((user) =>
                                                    selectedUserIds.includes(user._id)
                                                )
                                                    ? "Unselect Page"
                                                    : "Select Page"}
                                            </button>

                                            <button
                                                type="button"
                                                onClick={selectAllUsers}
                                                disabled={selectingAllUsers || userTotal === 0}
                                                className="text-sm font-semibold text-emerald-700 hover:text-emerald-900 disabled:opacity-50"
                                            >
                                                {selectingAllUsers
                                                    ? "Selecting All..."
                                                    : "Select All Users"}
                                            </button>

                                            {selectedUserIds.length > 0 && (
                                                <button
                                                    type="button"
                                                    onClick={clearSelectedUsers}
                                                    disabled={selectingAllUsers}
                                                    className="text-sm font-semibold text-red-600 hover:text-red-800 disabled:opacity-50"
                                                >
                                                    Clear Selection
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex flex-col sm:flex-row gap-2 mb-3">
                                        <input
                                            type="text"
                                            value={userSearchInput}
                                            onChange={(event) =>
                                                setUserSearchInput(event.target.value)
                                            }
                                            onKeyDown={(event) => {
                                                if (event.key === "Enter") {
                                                    event.preventDefault();
                                                    handleUserSearch();
                                                }
                                            }}
                                            placeholder="Search name, email, or phone..."
                                            className="flex-1 border border-gray-300 rounded-xl px-4 py-2 outline-none focus:ring-2 focus:ring-violet-500"
                                        />
                                        <button
                                            type="button"
                                            onClick={handleUserSearch}
                                            className="bg-gray-900 text-white rounded-xl px-4 py-2 font-medium"
                                        >
                                            Search
                                        </button>
                                        {(userSearch || userSearchInput) && (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setUserSearchInput("");
                                                    setUserSearch("");
                                                    setUserPage(1);
                                                }}
                                                className="border rounded-xl px-4 py-2 font-medium"
                                            >
                                                Clear
                                            </button>
                                        )}
                                    </div>

                                    {usersError ? (
                                        <div className="border border-red-200 bg-red-50 text-red-700 rounded-xl p-3 text-sm">
                                            {usersError}
                                            <button
                                                type="button"
                                                onClick={fetchUsers}
                                                className="ml-3 font-semibold underline"
                                            >
                                                Retry
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="border rounded-xl overflow-hidden">
                                            <div className="max-h-64 overflow-y-auto divide-y">
                                                {usersLoading ? (
                                                    <div className="p-5 text-center text-gray-500">
                                                        Loading users...
                                                    </div>
                                                ) : users.length === 0 ? (
                                                    <div className="p-5 text-center text-gray-500">
                                                        No users found
                                                    </div>
                                                ) : (
                                                    users.map((user) => (
                                                        <label
                                                            key={user._id}
                                                            className="flex items-center gap-3 p-3 hover:bg-violet-50 cursor-pointer"
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedUserIds.includes(user._id)}
                                                                onChange={() => toggleUser(user._id)}
                                                                className="h-4 w-4 accent-violet-600"
                                                            />
                                                            <div className="min-w-0 flex-1">
                                                                <div className="font-semibold text-gray-900 truncate">
                                                                    {user.full_name || "Unnamed customer"}
                                                                </div>
                                                                <div className="text-xs text-gray-500 truncate">
                                                                    {[user.phone_prefix, user.phone_number]
                                                                        .filter(Boolean)
                                                                        .join(" ") ||
                                                                        user.email ||
                                                                        user._id}
                                                                </div>
                                                            </div>
                                                            <div className="text-sm font-semibold text-emerald-700">
                                                                ₹{Number(user.wallet_balance || 0).toFixed(2)}
                                                            </div>
                                                        </label>
                                                    ))
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    <div className="flex items-center justify-center gap-3 mt-3">
                                        <button
                                            type="button"
                                            disabled={userPage <= 1 || usersLoading}
                                            onClick={() =>
                                                setUserPage((current) => current - 1)
                                            }
                                            className="border rounded-lg px-3 py-2 text-sm disabled:opacity-40"
                                        >
                                            ← Previous
                                        </button>
                                        <span className="text-sm text-gray-500">
                                            Page {userPage} of {userTotalPages}
                                        </span>
                                        <button
                                            type="button"
                                            disabled={
                                                userPage >= userTotalPages || usersLoading
                                            }
                                            onClick={() =>
                                                setUserPage((current) => current + 1)
                                            }
                                            className="border rounded-lg px-3 py-2 text-sm disabled:opacity-40"
                                        >
                                            Next →
                                        </button>
                                    </div>
                                </div>

                            </div>

                            {/* MODAL FOOTER */}
                            <div className="border-t px-6 py-4 flex justify-end gap-3">

                                <button
                                    onClick={() => {
                                        setShowModal(false);
                                        resetForm();
                                    }}
                                    className="px-5 py-3 border rounded-xl font-medium hover:bg-gray-50"
                                >
                                    Cancel
                                </button>

                                <button
                                    onClick={handleSave}
                                    disabled={saving}
                                    className="px-5 py-3 bg-violet-600 hover:bg-violet-700 text-white rounded-xl font-semibold disabled:opacity-50"
                                >
                                    {saving
                                        ? "Saving..."
                                        : editingId
                                            ? "Update Notification"
                                            : "Create Notification"}
                                </button>

                            </div>

                        </div>

                    </div>

                )}

            </div>
        </div>
    );
}
